import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
// Material imports
import Email from "@mui/icons-material/Email";
import Create from "@mui/icons-material/Create";
import Person from "@mui/icons-material/Person";
import PhoneIphone from "@mui/icons-material/PhoneIphone";
import makeStyles from "@mui/styles/makeStyles";
// Limitless imports
import { useCustomerContext, useCustomerMessageContext } from "@limitless/customer/hooks";
import { InputField, SubmitButton } from "@limitless/customer/components";
import { ClientContext } from "@limitless/common/utils";
import { useMobileDetectionContext } from "../../hooks/mobile-detection/MobileDetectionContext";
import ClientSecureEmailLabel from "../client-secure-email-label/ClientSecureEmailLabel";
import InputDropdown from "../input-dropdown/InputDropdown";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        marginLeft: 20,
        paddingRight: 20,
        paddingTop: 10,
        overflowY: "scroll",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
            width: "0px",
            background: "transparent"
        },
        "&::-webkit-scrollbar-thumb": {
            background: "transparent"
        },
        "&::-webkit-scrollbar-track": {
            background: "transparent"
        }
    },
    icon: {
        marginLeft: props => (props.isRtl ? "0" : "4px"),
        marginRight: props => (props.isRtl ? "20px" : "0")
    }
});

const getNumberOfRows = isMobile => {
    if (ClientContext._userMobileEnabled === "true" || ClientContext._userMobileEnabled === true) {
        return 1;
    }

    return isMobile ? 2 : 4;
};

const CreateMessageForm = ({ llmConfig }) => {
    const { isMobileDevice } = useMobileDetectionContext();
    const numberOfRows = getNumberOfRows(isMobileDevice);
    const { updateCustomer, customer, getCustomerData } = useCustomerContext();

    const {
        name,
        email,
        question,
        tenantSubject,
        mobileNumber,
        customerLastName,
        isFormInvalid,
        fieldErrors,
        secondaryFieldErrors
    } = customer;

    const { message, processMessageBasedOnRule, dialogOpen, createMessage } = useCustomerMessageContext();
    const { loading } = message;

    const isRtl = document.documentElement.getAttribute("dir") === "rtl";

    const classes = useStyles({ isRtl });
    const history = useHistory();

    const { attribute } = llmConfig || {};
    const newMessageRule = attribute?.data_new_message_rules_new_message;
    const reContactMessageRule = attribute?.data_new_message_rules_recontact_llm;

    React.useEffect(() => {
        if (allUserCaptureSupress && !shouldRenderMobileField) {
            history.push("/newMessage");
        }
    }, []);

    const handleSubmit = () => {
        // Check if either reContactMessageRule or newMessageRule is configured at tenant level
        if (reContactMessageRule || newMessageRule) {
            processMessageBasedOnRule(reContactMessageRule, newMessageRule, response => {
                if (response) {
                    history.push(`/conversation/${response.messageId}/${response.accessKey}/false`);
                }
            });
        } else {
            // If no rules are provided, create a new message using customer data
            createMessage(getCustomerData(), newMsg => {
                history.push(`/conversation/${newMsg.messageId}/${newMsg.accessKey}/${false}`);
            });
        }
    };

    const renderMobileTxt = () => {
        return (
            <InputField
                label="Mobile"
                icon={<PhoneIphone style={{ paddingBottom: 5, paddingLeft: 2 }} />}
                toolTipTitle="Add your mobile number"
                errorMessage={fieldErrors.mobileNumber}
                onChangeHandle={value => updateCustomer("mobileNumber", value)}
                loading={loading}
                value={mobileNumber.value}
                noGutterBottom
            />
        );
    };

    const allUserCaptureSupress = ClientContext._userCaptureSuppress === "all";
    const allOrEmailCaptureSupress =
        ClientContext._userCaptureSuppress === "all" || ClientContext._userCaptureSuppress === "email";

    const shouldRenderMobileField =
        ClientContext._userMobileEnabled === "true" || ClientContext._userMobileEnabled === true ? true : false;
    const shouldRenderSubjects = llmConfig !== null && llmConfig?.attribute.subjects;
    const shouldRenderLastNameField = llmConfig !== null && llmConfig?.attribute.capture_customer_surname;
    const shouldRenderNameField = ClientContext._userCaptureSuppress === "name" || allUserCaptureSupress ? true : false;
    const shouldRenderEmailField =
        ClientContext._userCaptureSuppress === "email" || allUserCaptureSupress ? true : false;
    const isSubject = shouldRenderSubjects && tenantSubject.value === "" ? true : false;
    const isLastName = shouldRenderLastNameField && !customerLastName.value ? true : false;

    return (
        <>
            <div className={classes.container}>
                {!shouldRenderNameField && (
                    <InputField
                        label={shouldRenderLastNameField ? "First Name" : "Name"}
                        icon={<Person className={classes.icon} />}
                        toolTipTitle="Who the expert should address"
                        errorMessage={fieldErrors.name}
                        onChangeHandle={value => updateCustomer("name", value)}
                        loading={loading}
                        value={name.value}
                    />
                )}
                {shouldRenderLastNameField && (
                    <InputField
                        label="Last Name"
                        icon={<Person className={classes.icon} />}
                        toolTipTitle="Who the expert should address with last name"
                        errorMessage={fieldErrors.customerLastName}
                        onChangeHandle={value => updateCustomer("customerLastName", value)}
                        loading={loading}
                        value={customerLastName.value}
                    />
                )}
                {!shouldRenderEmailField && (
                    <InputField
                        label="Email"
                        icon={<Email className={classes.icon} />}
                        toolTipTitle="Receive email notifications about responses"
                        errorMessage={fieldErrors.email || secondaryFieldErrors?.email}
                        onChangeHandle={value => updateCustomer("email", value)}
                        loading={loading}
                        value={email.value}
                    />
                )}
                {shouldRenderSubjects && !dialogOpen && (
                    <InputDropdown
                        placeHolder="What is your question about?"
                        value={tenantSubject.value}
                        options={llmConfig?.attribute.subjects.split("|")}
                        errorMessage={fieldErrors.tenantSubject}
                        onChange={value => updateCustomer("tenantSubject", value)}
                    />
                )}
                {shouldRenderMobileField && renderMobileTxt()}
                <InputField
                    label="Question"
                    icon={<Create style={{ paddingLeft: 4 }} />}
                    toolTipTitle="Add as much detail as possible"
                    errorMessage={fieldErrors.question}
                    onChangeHandle={value => updateCustomer("question", value)}
                    loading={loading}
                    value={question.value}
                    multiline={!shouldRenderMobileField}
                    rows={numberOfRows}
                />
                {allOrEmailCaptureSupress && <ClientSecureEmailLabel />}
                <SubmitButton
                    disabled={loading || isFormInvalid || isSubject || isLastName}
                    title="Submit Your Question"
                    handleSubmit={handleSubmit}
                    loading={loading}
                />
            </div>
        </>
    );
};

CreateMessageForm.propTypes = {
    llmConfig: PropTypes.object,
    setDialogOpen: PropTypes.bool,
    dialogOpen: PropTypes.bool
};

export default CreateMessageForm;
